import { action, observable } from 'mobx';
import { FormikHelpers } from 'formik';
import merge from 'lodash.merge';

import {
  API_ACCOUNT_DETAIL_EXT_PRODUCT,
  API_ACCOUNT_DETAIL_EXT_PRODUCT_ACCOUNTS,
  API_ACCOUNT_DETAIL_EXT_PRODUCT_CALCULATE,
  API_ACCOUNT_DETAIL_EXT_PRODUCT_SAVE,
  API_ACCOUNT_DETAIL_EXT_PRODUCT_VALIDATE,
  API_ACCOUNT_DETAIL_EXT_PRODUCTS,
  API_ACCOUNT_DETAIL_EXT_PRODUCTS_DOWNLOAD,
  API_ACCOUNT_DETAIL_EXT_PRODUCTS_TABLE,
  API_ACCOUNT_DETAIL_FIN_INST_LOOKUP,
  API_ACCOUNT_EXT_PRODUCT,
  API_ACCOUNT_EXT_PRODUCT_ACCOUNTS,
  API_ACCOUNT_EXT_PRODUCT_CALCULATE,
  API_ACCOUNT_EXT_PRODUCT_SAVE,
  API_ACCOUNT_EXT_PRODUCT_VALIDATE,
  API_ACCOUNT_EXT_PRODUCTS,
  API_ACCOUNT_EXT_PRODUCTS_DOWNLOAD,
  API_ACCOUNT_EXT_PRODUCTS_REFRESH,
  API_ACCOUNT_EXT_PRODUCTS_TABLE,
  API_ACCOUNT_FIN_INST_LOOKUP,
  API_FUNCTION_TYPE,
  API_FUNCTION_TYPE_TARGET,
} from 'api/endpoints';

import { FormStore } from 'stores/formStore';
import { RootStore } from 'stores/rootStore';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';

import {
  ExternalProductData,
  ExternalProductsData,
  ExtProductAsset,
  ExtProductCategory,
  ExtProductRegion,
  FinancialInstitution,
  LinkableAccount,
} from 'pages/Accounts/Edit/components/ExternalProducts/model';
import { Currency, Order } from 'enums';
import { f2p } from 'helpers/helpers';

const EXTPRODS_STATE = 'extProdsState';

/*
export interface LookupResult {
  id: string;
  title: string;
  internal: boolean;
}
*/

interface ProductsTableState {
  orderBy?: number;
  order?: Order;
}

export class ExternalProductsStore extends FormStore<ExternalProductData> {
  accountId: string;
  //prefixDepth: number;
  endpointProducts: API_FUNCTION_TYPE;
  endpointDownload: API_FUNCTION_TYPE;
  endpointProduct: API_FUNCTION_TYPE_TARGET;
  endpointProductSave: API_FUNCTION_TYPE;
  endpointProductValidate: API_FUNCTION_TYPE;
  endpointProductCalculate: API_FUNCTION_TYPE_TARGET;
  endpointAccounts: API_FUNCTION_TYPE_TARGET;
  endpointLookup: API_FUNCTION_TYPE_TARGET;
  endpointTable: API_FUNCTION_TYPE;

  @observable allProducts?: ExternalProductsData;
  @observable lookupOptions: FinancialInstitution[] = [];
  @observable calculating = false;
  @observable calculatingValue = false;
  @observable calculatingLoan = false;
  @observable dataPrepared = false;
  @observable invalidLink = false;
  @observable accounts: LinkableAccount[] = [];
  @observable loading = false;

  @observable tableState: ProductsTableState = {};

  constructor(rootStore: RootStore, accountId: string, viewMode?: ViewMode) {
    super(rootStore);
    this.accountId = accountId;
    this.allProducts = undefined;

    switch (viewMode) {
      case ViewMode.DETAIL:
        this.endpointProduct = API_ACCOUNT_DETAIL_EXT_PRODUCT;
        this.endpointProductSave = API_ACCOUNT_DETAIL_EXT_PRODUCT_SAVE;
        this.endpointProductValidate = API_ACCOUNT_DETAIL_EXT_PRODUCT_VALIDATE;
        this.endpointProductCalculate = API_ACCOUNT_DETAIL_EXT_PRODUCT_CALCULATE;
        this.endpointProducts = API_ACCOUNT_DETAIL_EXT_PRODUCTS;
        this.endpointDownload = API_ACCOUNT_DETAIL_EXT_PRODUCTS_DOWNLOAD;
        this.endpointAccounts = API_ACCOUNT_DETAIL_EXT_PRODUCT_ACCOUNTS;
        this.endpointLookup = API_ACCOUNT_DETAIL_FIN_INST_LOOKUP;
        this.endpointTable = API_ACCOUNT_DETAIL_EXT_PRODUCTS_TABLE;
        break;
      default:
        this.endpointProduct = API_ACCOUNT_EXT_PRODUCT;
        this.endpointProductSave = API_ACCOUNT_EXT_PRODUCT_SAVE;
        this.endpointProductValidate = API_ACCOUNT_EXT_PRODUCT_VALIDATE;
        this.endpointProductCalculate = API_ACCOUNT_EXT_PRODUCT_CALCULATE;
        this.endpointProducts = API_ACCOUNT_EXT_PRODUCTS;
        this.endpointDownload = API_ACCOUNT_EXT_PRODUCTS_DOWNLOAD;
        this.endpointAccounts = API_ACCOUNT_EXT_PRODUCT_ACCOUNTS;
        this.endpointLookup = API_ACCOUNT_FIN_INST_LOOKUP;
        this.endpointTable = API_ACCOUNT_EXT_PRODUCTS_TABLE;
        break;
    }
  }

  @action setLoading = (loading: boolean) => (this.loading = loading);

  @action
  refreshProducts = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.rootStore.stores.apiStore.patch(API_ACCOUNT_EXT_PRODUCTS_REFRESH(this.accountId));
    this.rootStore.stores.uiStore.setLoading(false);
    await this.fetchAllProducts();
  };

  @action
  downloadProducts = async () => {
    const { get } = this.rootStore.stores.apiStore;
    const { uiStore } = this.rootStore.stores;
    uiStore.setLoading(true);
    await get(this.endpointDownload(this.accountId));
    uiStore.setLoading(false);
  };

  fetchAllProducts = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<ExternalProductsData>(
      this.endpointProducts(this.accountId),
    );
    if (response?.data) {
      this.allProducts = response.data;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  fetchProduct = async (productId: string) => {
    //this.rootStore.stores.uiStore.setLoading(true);
    await this.fetchData(this.endpointProduct(this.accountId, productId));
    this.convertValuesForFE();
    if (this.data?.invalidLink) {
      this.data = null;
      this.invalidLink = true;
    }
    this.dataPrepared = true;
    //this.rootStore.stores.uiStore.setLoading(false);
  };

  deleteProduct = async (targetId: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.rootStore.stores.apiStore.delete(this.endpointProduct(this.accountId, targetId));
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action
  convertValuesForFE = () => {
    if (this.data) {
      this.data.financialInstitution?.icon && delete this.data.financialInstitution.icon;
      this.data.financialInstitution?.logo && delete this.data.financialInstitution.logo;
      this.data.interest = f2p(this.data.interest);
      if (!this.data.useValues) {
        Object.keys(this.data.asset).forEach(
          (k) => (this.data!.asset[k as ExtProductAsset]! = f2p(this.data!.asset[k as ExtProductAsset]!)),
        );
        Object.keys(this.data.region).forEach(
          (k) => (this.data!.region[k as ExtProductRegion]! = f2p(this.data!.region[k as ExtProductRegion]!)),
        );
        Object.keys(this.data.currency).forEach(
          (k) => (this.data!.currency[k as Currency]! = f2p(this.data!.currency[k as Currency]!)),
        );
      }
    }
  };

  convertValuesForBE = (values: ExternalProductData) => {
    const convertedValues = JSON.parse(JSON.stringify(values)) as ExternalProductData;
    convertedValues.interest /= 100;
    if ((values.useValues && !values.appliedValues) || (!values.useValues && !values.appliedValues)) {
      Object.keys(convertedValues.asset).forEach((k) => (convertedValues.asset[k as ExtProductAsset]! /= 100));
      Object.keys(convertedValues.region).forEach((k) => (convertedValues.region[k as ExtProductRegion]! /= 100));
      Object.keys(convertedValues.currency).forEach((k) => (convertedValues.currency[k as Currency]! /= 100));
    }
    return convertedValues;
  };

  @action
  calculateValue = async (values: ExternalProductData, formikHelpers: FormikHelpers<ExternalProductData>) => {
    this.calculatingValue = true;
    const convertedValues = this.convertValuesForBE(values);
    const response = await this.rootStore.stores.apiStore.patch<ExternalProductData>(
      this.endpointProductCalculate(this.accountId, 'VALUE'),
      convertedValues,
      true,
    );
    if (response?.data) {
      formikHelpers.setFieldValue('value', response.data.value);
    }
    this.calculatingValue = false;
  };

  @action
  calculateLoan = async (values: ExternalProductData, formikHelpers: FormikHelpers<ExternalProductData>) => {
    this.calculatingLoan = true;
    const convertedValues = this.convertValuesForBE(values);
    const response = await this.rootStore.stores.apiStore.patch<ExternalProductData>(
      this.endpointProductCalculate(this.accountId, 'LOAN'),
      convertedValues,
      true,
    );
    if (response?.data) {
      formikHelpers.setFieldValue('periodic.value', response.data.periodic?.value);
      formikHelpers.setFieldValue('value.value', response.data.periodic?.value?.value);
    }
    this.calculatingLoan = false;
  };

  @action
  model = async (values: ExternalProductData) => {
    this.calculating = true;
    const convertedValues = this.convertValuesForBE(values);
    const response = await this.rootStore.stores.apiStore.patch<ExternalProductData>(
      this.endpointProductCalculate(this.accountId, 'FIELDS'),
      convertedValues,
      true,
    );
    if (response?.data) {
      this.data = response.data;
      this.convertValuesForFE();
    } else {
      this.data = values;
    }
    this.calculating = false;
  };

  @action
  clearData = () => {
    this.data = null;
    this.dataPrepared = false;
    this.accounts = [];
    this.invalidLink = false;
  };

  handleSubmit = async (
    values: ExternalProductData,
    formikHelpers: FormikHelpers<ExternalProductData>,
    setup: boolean, // first step, validation only
    singleStep: boolean,
  ) => {
    const convertedValues = this.convertValuesForBE(values);
    let value = convertedValues.value.value;

    if (singleStep) {
      this.loading = true;
      const ok = await this.submitData(this.endpointProductSave(this.accountId), convertedValues, {
        formikHelpers,
        reset: false,
        singleValueResponse: true,
      });
      this.loading = false;
      return ok;
    }

    if (!setup && values.category !== ExtProductCategory.LOAN) {
      this.calculatingValue = true;
      const response = await this.rootStore.stores.apiStore.patch<ExternalProductData>(
        this.endpointProductCalculate(this.accountId, 'VALUE'),
        convertedValues,
        true,
      );
      this.calculatingValue = false;
      if (response?.data) {
        value = response?.data.value.value;
      } else {
        return false;
      }
    }

    if (
      setup || // first step, validation only
      value.toString() === convertedValues.value.value.toString() || // value already calculated and matches
      (values.category === ExtProductCategory.PROPERTY && !values.useTarget) // property not used in plan, skip calculation
    ) {
      return await this.submitData(
        setup ? this.endpointProductValidate(this.accountId) : this.endpointProductSave(this.accountId),
        convertedValues,
        {
          formikHelpers,
          reset: false,
          //validationPrefixDepth: this.prefixDepth,
          singleValueResponse: true,
        },
      );
    } else {
      formikHelpers.setFieldValue('value.value', value);
      return false;
    }
  };

  @action
  fetchLookupOptions = async () => {
    const response = await this.rootStore.stores.apiStore.get<FinancialInstitution[]>(
      this.endpointLookup(this.accountId),
    );
    if (response?.data) {
      this.lookupOptions = response.data;
    }
  };

  @action
  clearLookupOptions = () => {
    this.lookupOptions = [];
  };

  @action
  getAccounts = async (productId?: string) => {
    const response = await this.rootStore.stores.apiStore.get<LinkableAccount[]>(
      this.endpointAccounts(this.accountId, productId ? `?id=${productId}` : ''),
    );
    if (response?.data) {
      this.accounts = response.data.filter((account) => account.available);
    }
  };

  @action
  linkAccount = async (
    currentValues: ExternalProductData,
    accountId: string,
    currency: Currency,
    productId?: string,
  ) => {
    const response = await this.rootStore.stores.apiStore.post<ExternalProductData>(
      this.endpointAccounts(this.accountId, productId ? `?id=${productId}` : ''),
      { accountId, accountCurrency: currency },
    );
    if (response?.data) {
      this.data = merge({}, this.convertValuesForBE(currentValues), response.data);
      this.convertValuesForFE();

      return true;
      //this.accounts = response.data.filter(account => account.available);
    } else {
      return false;
    }
  };

  @action onTableOrderChange = (orderedColumnId: number, orderDirection: Order) => {
    const state = this.tableState;
    state.orderBy = orderedColumnId;
    state.order = orderDirection;
    this.tableState = state;
    localStorage.setItem(EXTPRODS_STATE, JSON.stringify(state));
  };

  setTableView = async (view: boolean) => {
    this.rootStore.stores.apiStore.post<ExternalProductData>(this.endpointTable(this.accountId), { value: view });
  };

  sortTable = <T extends any>(columns: T[]): T[] => {
    if (!Array.isArray(columns)) return columns;
    return columns.map((c: any, index) => ({
      ...c,
      defaultSort: index === this.tableState.orderBy ? this.tableState.order : undefined,
    }));
  };
}
